import { create } from 'zustand';
import { useEffect } from 'react';
import { useDorisSummary } from 'src/serverProxy/useDorisSummary';

interface TrafficSummaryData {
  metricType: string;
  totalClicks: number;
  organicClicks: number;
  adClicks: number;
  otherClicks: number;
  debug?: {
    query: string;
    queryExecutionTime: number;
    queryContextCreationTime: number;
    isCacheHit: boolean;
    cacheResponseTime: number;
    topicGroupAnnotationTime: number;
  };
}

interface TrafficMetricsState {
  // Data state
  trafficSummary: TrafficSummaryData | null;
  comparisonTrafficSummary: TrafficSummaryData | null;
  isLoading: boolean;

  // Actions
  setTrafficSummary: (data: TrafficSummaryData) => void;
  setComparisonTrafficSummary: (data: TrafficSummaryData) => void;
  setIsLoading: (loading: boolean) => void;

  // Selectors
  getTrafficValue: (trafficType: TrafficType) => number;
  getComparisonTrafficValue: (trafficType: TrafficType) => number;
}

type TrafficType = 'totalClicks' | 'organicClicks' | 'adClicks' | 'otherClicks';

/**
 * Zustand store for traffic metrics data
 * This store is populated by the parent component that fetches traffic summary data
 * and provides that data to child components through selectors
 */
export const useTrafficMetricsStore = create<TrafficMetricsState>((set, get) => ({
  // Initial state
  trafficSummary: null,
  comparisonTrafficSummary: null,
  isLoading: false,

  // Actions
  setTrafficSummary: (data: TrafficSummaryData) => {
    set({ trafficSummary: data });
  },

  setComparisonTrafficSummary: (data: TrafficSummaryData) => {
    set({ comparisonTrafficSummary: data });
  },

  setIsLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  // Selectors
  getTrafficValue: (trafficType: TrafficType) => {
    const { trafficSummary } = get();

    if (!trafficSummary) {
      return 0;
    }

    // Return the appropriate traffic value based on the type
    switch (trafficType) {
      case 'totalClicks':
        return trafficSummary.totalClicks;
      case 'organicClicks':
        return trafficSummary.organicClicks;
      case 'adClicks':
        return trafficSummary.adClicks;
      case 'otherClicks':
        return trafficSummary.otherClicks;
      default:
        return 0;
    }
  },

  getComparisonTrafficValue: (trafficType: TrafficType) => {
    const { comparisonTrafficSummary } = get();

    if (!comparisonTrafficSummary) {
      return 0;
    }

    // Return the appropriate traffic value based on the type
    switch (trafficType) {
      case 'totalClicks':
        return comparisonTrafficSummary.totalClicks;
      case 'organicClicks':
        return comparisonTrafficSummary.organicClicks;
      case 'adClicks':
        return comparisonTrafficSummary.adClicks;
      case 'otherClicks':
        return comparisonTrafficSummary.otherClicks;
      default:
        return 0;
    }
  }
}));

/**
 * Custom hook to fetch traffic metrics with filtering capability
 * This hook provides a function that components can call to fetch traffic data
 * with specific filters applied
 */
export const useTrafficMetricsWithFilters = () => {
  const setTrafficSummary = useTrafficMetricsStore((state) => state.setTrafficSummary);
  const setComparisonTrafficSummary = useTrafficMetricsStore((state) => state.setComparisonTrafficSummary);
  const setIsLoading = useTrafficMetricsStore((state) => state.setIsLoading);

  // Use the Doris Summary hook to get real data, but only if we haven't loaded data yet

  const dorisSummary = useDorisSummary({
    requestName: 'totalTrafficMarketShareTotal',
    metricType: 'ClicksSummary',
    retailerId: 1,
    interceptBaseParams: (params) => {
      return {
        ...params,
        brandIds: []
      };
    },
    interceptComparisonParams: (params) => {
      return {
        ...params,
        brandIds: []
      };
    },

    queryOptions: {
      enabled: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Extract both primary and comparison data
  const { primaryData, comparisonData, isLoading: isDorisLoading } = dorisSummary;

  // Update the store with Doris data when it changes
  useEffect(() => {
    // Set loading state
    setIsLoading(isDorisLoading);

    // Only update when data is available
    if (primaryData) {
      setTrafficSummary({
        metricType: 'traffic',
        totalClicks: primaryData?.totalClicks || 0,
        organicClicks: primaryData?.organicClicks || 0,
        adClicks: primaryData?.adClicks || 0,
        otherClicks: primaryData?.otherClicks || 0
      });
    }

    if (comparisonData) {
      setComparisonTrafficSummary({
        metricType: 'traffic',
        totalClicks: comparisonData?.totalClicks || 0,
        organicClicks: comparisonData?.organicClicks || 0,
        adClicks: comparisonData?.adClicks || 0,
        otherClicks: comparisonData?.otherClicks || 0
      });
    }
  }, [primaryData, comparisonData, isDorisLoading, setTrafficSummary, setComparisonTrafficSummary, setIsLoading]);

  return {
    isLoading: isDorisLoading
  };
};
